/**
 * Auth Operations
 *
 * Server-side authentication operations using Saleor GraphQL API.
 * Following the same patterns as lib/checkout/operations.ts
 */

import {
  AccountRegisterDocument,
  ConfirmAccountDocument,
  MeDocument,
  RequestPasswordResetDocument,
  SetPasswordDocument,
  TokenCreateDocument,
  TokenRefreshDocument,
  type SaleorAccountErrorFragment,
  type SaleorAccountRegisterMutation,
  type SaleorAccountRegisterMutationVariables,
  type SaleorAddressFragment,
  type SaleorAuthUserFragment,
  type SaleorConfirmAccountMutation,
  type SaleorConfirmAccountMutationVariables,
  type SaleorRequestPasswordResetMutation,
  type SaleorRequestPasswordResetMutationVariables,
  type SaleorSetPasswordMutation,
  type SaleorSetPasswordMutationVariables,
  type SaleorTokenCreateMutation,
  type SaleorTokenCreateMutationVariables,
  type SaleorTokenRefreshMutation,
  type SaleorTokenRefreshMutationVariables,
} from '@/graphql/saleor/api';
import { saleorMutate, saleorQuery } from '@/lib/graphql/client';
import { devError, devLog } from '@/lib/utils/devLog';
import type { AuthError, AuthTokens, AuthUser, UserAddress } from './types';

/**
 * Result of a login operation
 */
export interface LoginResult {
  success: boolean;
  tokens?: AuthTokens;
  user?: AuthUser;
  errors?: AuthError[];
}

/**
 * Result of a token refresh operation
 */
export interface RefreshResult {
  success: boolean;
  accessToken?: string;
  errors?: AuthError[];
}

/**
 * Result of a registration operation
 */
export interface RegisterResult {
  success: boolean;
  user?: AuthUser;
  requiresConfirmation?: boolean;
  errors?: AuthError[];
}

/**
 * Result of confirming an account via email link
 */
export interface ConfirmAccountResult {
  success: boolean;
  errors?: AuthError[];
}

/**
 * Result of a password reset request
 */
export interface RequestPasswordResetResult {
  success: boolean;
  errors?: AuthError[];
}

/**
 * Result of setting a new password via token
 */
export interface SetPasswordResult {
  success: boolean;
  tokens?: AuthTokens;
  user?: AuthUser;
  errors?: AuthError[];
}

function mapSaleorAddress(address: SaleorAddressFragment): UserAddress {
  return {
    id: address.id,
    firstName: address.firstName,
    lastName: address.lastName,
    companyName: address.companyName || undefined,
    streetAddress1: address.streetAddress1,
    streetAddress2: address.streetAddress2 || undefined,
    city: address.city,
    cityArea: address.cityArea || undefined,
    postalCode: address.postalCode,
    countryArea: address.countryArea || undefined,
    phone: address.phone || undefined,
    country: {
      code: address.country.code,
      country: address.country.country,
    },
    isDefaultBillingAddress: address.isDefaultBillingAddress ?? undefined,
    isDefaultShippingAddress: address.isDefaultShippingAddress ?? undefined,
    metadata: address.metadata.map((m) => ({ key: m.key, value: m.value })),
  };
}

/**
 * Transform Saleor user fragment to our AuthUser type
 */
function transformUser(saleorUser: SaleorAuthUserFragment): AuthUser {
  return {
    id: saleorUser.id,
    email: saleorUser.email,
    firstName: saleorUser.firstName,
    lastName: saleorUser.lastName,
    metadata: saleorUser.metadata.map((m) => ({
      key: m.key,
      value: m.value,
    })),
    addresses: saleorUser.addresses.map(mapSaleorAddress),
    defaultBillingAddress: saleorUser.defaultBillingAddress
      ? { ...mapSaleorAddress(saleorUser.defaultBillingAddress), isDefaultBillingAddress: true }
      : null,
    defaultShippingAddress: saleorUser.defaultShippingAddress
      ? { ...mapSaleorAddress(saleorUser.defaultShippingAddress), isDefaultShippingAddress: true }
      : null,
  };
}

/**
 * Transform Saleor errors to our AuthError type
 */
function transformErrors(saleorErrors: SaleorAccountErrorFragment[]): AuthError[] {
  return saleorErrors.map((e) => ({
    field: e.field,
    message: e.message,
    code: e.code,
  }));
}

/**
 * Login with email and password
 *
 * Calls Saleor tokenCreate mutation to authenticate the user.
 * Returns tokens and user data on success, errors on failure.
 */
export async function login(email: string, password: string): Promise<LoginResult> {
  devLog('[Auth] Attempting login for:', email);

  try {
    const result = await saleorMutate<
      SaleorTokenCreateMutation,
      SaleorTokenCreateMutationVariables
    >(TokenCreateDocument, { email, password });

    const { tokenCreate } = result;

    if (!tokenCreate) {
      devError('[Auth] Login failed - no response from tokenCreate');
      return {
        success: false,
        errors: [
          {
            field: null,
            message: 'Authentication failed - no response',
            code: 'INVALID_RESPONSE',
          },
        ],
      };
    }

    if (tokenCreate.errors && tokenCreate.errors.length > 0) {
      devLog('[Auth] Login failed with errors:', tokenCreate.errors);
      return {
        success: false,
        errors: transformErrors(tokenCreate.errors),
      };
    }

    if (!tokenCreate.token || !tokenCreate.refreshToken || !tokenCreate.user) {
      devError('[Auth] Login succeeded but missing token or user data');
      return {
        success: false,
        errors: [
          {
            field: null,
            message: 'Authentication failed - missing token or user data',
            code: 'INVALID_RESPONSE',
          },
        ],
      };
    }

    devLog('[Auth] Login successful for:', email);
    return {
      success: true,
      tokens: {
        accessToken: tokenCreate.token,
        refreshToken: tokenCreate.refreshToken,
        csrfToken: tokenCreate.csrfToken ?? undefined,
      },
      user: transformUser(tokenCreate.user),
    };
  } catch (error) {
    devError('[Auth] Login error:', error);
    return {
      success: false,
      errors: [
        {
          field: null,
          message: error instanceof Error ? error.message : 'An unexpected error occurred',
          code: 'INTERNAL_ERROR',
        },
      ],
    };
  }
}

/**
 * Refresh the access token using a refresh token
 *
 * Calls Saleor tokenRefresh mutation to get a new access token.
 */
export async function refreshAccessToken(refreshToken: string): Promise<RefreshResult> {
  devLog('[Auth] Refreshing access token');

  try {
    const result = await saleorMutate<
      SaleorTokenRefreshMutation,
      SaleorTokenRefreshMutationVariables
    >(TokenRefreshDocument, { refreshToken });

    const { tokenRefresh } = result;

    if (tokenRefresh?.errors && tokenRefresh.errors.length > 0) {
      devLog('[Auth] Token refresh failed with errors:', tokenRefresh.errors);
      return {
        success: false,
        errors: transformErrors(tokenRefresh.errors),
      };
    }

    if (!tokenRefresh?.token) {
      devError('[Auth] Token refresh succeeded but missing new token');
      return {
        success: false,
        errors: [
          {
            field: null,
            message: 'Token refresh failed - missing new token',
            code: 'INVALID_RESPONSE',
          },
        ],
      };
    }

    devLog('[Auth] Token refresh successful');
    return {
      success: true,
      accessToken: tokenRefresh.token,
    };
  } catch (error) {
    devError('[Auth] Token refresh error:', error);
    return {
      success: false,
      errors: [
        {
          field: null,
          message: error instanceof Error ? error.message : 'An unexpected error occurred',
          code: 'INTERNAL_ERROR',
        },
      ],
    };
  }
}

/**
 * Get the current user using an access token (with auth header)
 *
 * This version creates a direct GraphQL request with the auth token.
 */
export async function getCurrentUserWithToken(accessToken: string): Promise<AuthUser | null> {
  devLog('[Auth] Getting current user with token');

  try {
    const result = await saleorQuery(MeDocument, undefined, accessToken);

    if (!result.me) {
      devLog('[Auth] No user found (not authenticated or token invalid)');
      return null;
    }

    devLog('[Auth] Current user retrieved:', result.me.email);
    return transformUser(result.me);
  } catch (error) {
    devError('[Auth] Get current user error:', error);
    return null;
  }
}

/**
 * Register a new account
 *
 * Calls Saleor accountRegister mutation. The registration may require
 * email confirmation depending on Saleor configuration.
 */
export async function register(input: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  channel: string;
  redirectUrl?: string;
  metadata?: Array<{ key: string; value: string }>;
}): Promise<RegisterResult> {
  devLog('[Auth] Attempting registration for:', input.email);

  try {
    const result = await saleorMutate<
      SaleorAccountRegisterMutation,
      SaleorAccountRegisterMutationVariables
    >(AccountRegisterDocument, {
      input: {
        email: input.email,
        password: input.password,
        firstName: input.firstName,
        lastName: input.lastName,
        channel: input.channel,
        redirectUrl: input.redirectUrl,
        metadata: input.metadata,
      },
    });

    const { accountRegister } = result;

    if (!accountRegister) {
      devError('[Auth] Registration failed - no response from accountRegister');
      return {
        success: false,
        errors: [
          {
            field: null,
            message: 'Registration failed - no response',
            code: 'INVALID_RESPONSE',
          },
        ],
      };
    }

    if (accountRegister.errors && accountRegister.errors.length > 0) {
      devLog('[Auth] Registration failed with errors:', accountRegister.errors);
      return {
        success: false,
        errors: transformErrors(accountRegister.errors),
      };
    }

    devLog('[Auth] Registration successful for:', input.email);
    return {
      success: true,
      user: accountRegister.user ? transformUser(accountRegister.user) : undefined,
      requiresConfirmation: accountRegister.requiresConfirmation ?? false,
    };
  } catch (error) {
    devError('[Auth] Registration error:', error);
    return {
      success: false,
      errors: [
        {
          field: null,
          message: error instanceof Error ? error.message : 'An unexpected error occurred',
          code: 'INTERNAL_ERROR',
        },
      ],
    };
  }
}

/**
 * Request a password reset email
 *
 * Calls Saleor requestPasswordReset mutation. Always returns success
 * to prevent email enumeration — Saleor silently ignores unknown emails.
 */
export async function requestPasswordReset(
  email: string,
  channel: string,
  redirectUrl: string
): Promise<RequestPasswordResetResult> {
  devLog('[Auth] Requesting password reset for:', email);

  try {
    const result = await saleorMutate<
      SaleorRequestPasswordResetMutation,
      SaleorRequestPasswordResetMutationVariables
    >(RequestPasswordResetDocument, { email, redirectUrl, channel });

    const { requestPasswordReset: resetResult } = result;

    if (resetResult?.errors && resetResult.errors.length > 0) {
      devLog('[Auth] Password reset request errors:', resetResult.errors);
      return {
        success: false,
        errors: transformErrors(resetResult.errors),
      };
    }

    devLog('[Auth] Password reset email sent for:', email);
    return { success: true };
  } catch (error) {
    devError('[Auth] Password reset request error:', error);
    return {
      success: false,
      errors: [
        {
          field: null,
          message: error instanceof Error ? error.message : 'An unexpected error occurred',
          code: 'INTERNAL_ERROR',
        },
      ],
    };
  }
}

/**
 * Set a new password using a reset token
 *
 * Calls Saleor setPassword mutation. On success, returns tokens
 * so the user can be automatically logged in.
 */
export async function setNewPassword(
  email: string,
  password: string,
  token: string
): Promise<SetPasswordResult> {
  devLog('[Auth] Setting new password for:', email);

  try {
    const result = await saleorMutate<
      SaleorSetPasswordMutation,
      SaleorSetPasswordMutationVariables
    >(SetPasswordDocument, { email, password, token });

    const { setPassword: setPasswordResult } = result;

    if (!setPasswordResult) {
      devError('[Auth] Set password failed - no response');
      return {
        success: false,
        errors: [
          {
            field: null,
            message: 'Set password failed - no response',
            code: 'INVALID_RESPONSE',
          },
        ],
      };
    }

    if (setPasswordResult.errors && setPasswordResult.errors.length > 0) {
      devLog('[Auth] Set password errors:', setPasswordResult.errors);
      return {
        success: false,
        errors: transformErrors(setPasswordResult.errors),
      };
    }

    devLog('[Auth] Password set successfully for:', email);
    return {
      success: true,
      tokens:
        setPasswordResult.token && setPasswordResult.refreshToken
          ? {
              accessToken: setPasswordResult.token,
              refreshToken: setPasswordResult.refreshToken,
            }
          : undefined,
      user: setPasswordResult.user ? transformUser(setPasswordResult.user) : undefined,
    };
  } catch (error) {
    devError('[Auth] Set password error:', error);
    return {
      success: false,
      errors: [
        {
          field: null,
          message: error instanceof Error ? error.message : 'An unexpected error occurred',
          code: 'INTERNAL_ERROR',
        },
      ],
    };
  }
}

/**
 * Confirm an account via the email confirmation link
 *
 * Calls Saleor confirmAccount mutation with the email + token from
 * the confirmation email URL. No auto-login — user must sign in after.
 */
export async function confirmAccount(email: string, token: string): Promise<ConfirmAccountResult> {
  devLog('[Auth] Confirming account for:', email);

  try {
    const result = await saleorMutate<
      SaleorConfirmAccountMutation,
      SaleorConfirmAccountMutationVariables
    >(ConfirmAccountDocument, { email, token });

    const { confirmAccount: confirmResult } = result;

    if (!confirmResult) {
      devError('[Auth] Confirm account failed - no response');
      return {
        success: false,
        errors: [
          {
            field: null,
            message: 'Account confirmation failed - no response',
            code: 'INVALID_RESPONSE',
          },
        ],
      };
    }

    if (confirmResult.errors && confirmResult.errors.length > 0) {
      devLog('[Auth] Confirm account errors:', confirmResult.errors);
      return {
        success: false,
        errors: transformErrors(confirmResult.errors),
      };
    }

    devLog('[Auth] Account confirmed successfully for:', email);
    return { success: true };
  } catch (error) {
    devError('[Auth] Confirm account error:', error);
    return {
      success: false,
      errors: [
        {
          field: null,
          message: error instanceof Error ? error.message : 'An unexpected error occurred',
          code: 'INTERNAL_ERROR',
        },
      ],
    };
  }
}
